<!--
  SupportDeskTicketView.svelte — one ticket, as an agent works it.

  The thread and composer take the main column; the side column holds the
  ticket's fields (status, priority, category, assignee — each saved the moment
  it changes, since there is nothing to batch), both SLA clocks, and who the
  requester is, with a link to their account for anything the ticket does not
  say.

  Internal notes appear in the thread in amber and the composer turns amber when
  writing one — see `SupportComposer` — so a note is never mistaken for a reply
  in either direction. Reply templates are those for this product plus the
  global ones, with their placeholders filled from this ticket as they insert.

  The People card lists the requester and anyone added to the ticket; an agent
  may add someone by the address of their existing account, or remove them.
-->
<script lang="ts">
	import { untrack } from 'svelte';
	import { Button, Label, Select, Spinner } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import {
		EmptyState,
		LicenseAppIcon,
		PageHeader,
		RelativeTime,
		RoutedButton,
		RoutedLink,
		SupportAttachmentsPanel,
		SupportComposer,
		SupportMessageThread,
		SupportPeopleCard,
		SupportPriorityBadge,
		SupportRating,
		SupportSLATimer,
		SupportStatusBadge,
		SupportTicketLinks
	} from '../../components';
	import type { SupportComposerMessage } from '../../components/SupportComposer.svelte';
	import {
		addDeskParticipant,
		deleteDeskAttachment,
		getDeskTicket,
		listReplyTemplates,
		listSupportAgents,
		listSupportCategories,
		removeDeskParticipant,
		replyDeskTicket,
		updateDeskTicket
	} from '../../AppNetwork';
	import type {
		SupportAttachment,
		SupportReplyTemplate,
		SupportCategory,
		SupportPerson,
		SupportTicket,
		SupportTicketPatch,
		SupportTicketPriority,
		SupportTicketStatus
	} from '../../AppTypes';
	import { tokenData } from '../../Auth';
	import {
		SUPPORT_PRIORITIES,
		SUPPORT_STATUSES,
		supportPriorityLabel,
		supportReasonText,
		supportStatusLabel
	} from '../../support/supportFormat';
	import { toastError, toastSuccess } from '../../toast';
	import { t, t2 } from '../../i18n';

	interface Props {
		/** Route params — which ticket. */
		params?: { ticketKey?: string };
	}

	let { params = {} }: Props = $props();

	/** The ticket on screen. */
	let ticket = $state<SupportTicket | null>(null);
	/** Whether it is loading. */
	let loading = $state(true);
	/** Failure of the read. */
	let error = $state('');
	/** Every agent, for assignment. */
	let agents: SupportPerson[] = $state([]);
	/** Categories offered for the ticket's product. */
	let categories: SupportCategory[] = $state([]);
	/** Reply templates for the ticket's product, plus the global ones. */
	let replyTemplates: SupportReplyTemplate[] = $state([]);
	/** Whether a message is being sent. */
	let sending = $state(false);
	/** Fraction of a message's attachments uploaded, while it is sending; `null` otherwise. */
	let uploadProgress = $state<number | null>(null);
	/** Whether a field change is being saved. */
	let saving = $state(false);
	/** Whether a change to who is on the ticket is in flight. */
	let changingPeople = $state(false);

	/** Loads the ticket, then what its side panel and composer offer. */
	async function load(): Promise<void> {
		loading = true;
		error = '';
		/** The pickers depend on the ticket's product, so they are read once it is known. */
		try {
			ticket = await getDeskTicket(params.ticketKey as string);
			await loadChoices(ticket.application.applicationUUID);
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load the ticket');
		} finally {
			loading = false;
		}
	}

	/**
	 * Re-reads the ticket in place, for news from the customer or another agent.
	 *
	 * Unlike {@link load} it leaves the page mounted, so a reply being written in
	 * the composer survives the refresh.
	 */
	async function refresh(): Promise<void> {
		/** A failed refresh keeps what is on screen and says so. */
		try {
			ticket = await getDeskTicket(params.ticketKey as string);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to load the ticket'));
		}
	}

	/** Loads agents, categories and reply templates; each one failing only empties its own picker. */
	async function loadChoices(applicationUUID: string): Promise<void> {
		const [agentList, categoryList, replyList] = await Promise.allSettled([
			listSupportAgents(),
			listSupportCategories(applicationUUID),
			listReplyTemplates(applicationUUID)
		]);
		agents = agentList.status === 'fulfilled' ? agentList.value : [];
		categories = categoryList.status === 'fulfilled' ? categoryList.value : [];
		replyTemplates = replyList.status === 'fulfilled' ? replyList.value : [];
	}

	/** Sends a reply or note, with the status change the agent chose. */
	async function send(message: SupportComposerMessage): Promise<boolean> {
		sending = true;
		/* Only a send with files shows a bar; without, the button's spinner is enough. */
		uploadProgress = message.files.length > 0 ? 0 : null;
		const onProgress = message.files.length > 0 ? (fraction: number) => (uploadProgress = fraction) : undefined;
		/** The response is the updated ticket, thread and status included. */
		try {
			ticket = await replyDeskTicket(
				params.ticketKey as string,
				{ body: message.body, isInternal: message.isInternal, status: message.status },
				message.files,
				onProgress
			);
			toastSuccess(message.isInternal ? t2('Note added.') : t2('Reply sent.'));
			return true;
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to send the message'));
			return false;
		} finally {
			sending = false;
			uploadProgress = null;
		}
	}

	/** Saves one field change. */
	async function patch(change: SupportTicketPatch): Promise<void> {
		saving = true;
		/** Each change is saved on its own; the response is the ticket with the event recorded. */
		try {
			ticket = await updateDeskTicket(params.ticketKey as string, change);
			toastSuccess(t2('Ticket updated.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to update the ticket'));
		} finally {
			saving = false;
		}
	}

	/** Adds someone by email. Resolves whether it worked, so the card knows to clear its field. */
	async function addPerson(email: string): Promise<boolean> {
		changingPeople = true;
		/** An unknown address comes back as a 400 whose message is worth showing as-is. */
		try {
			ticket = await addDeskParticipant(params.ticketKey as string, email);
			toastSuccess(t2('{{email}} was added to the ticket.', { email }));
			return true;
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to add the person'));
			return false;
		} finally {
			changingPeople = false;
		}
	}

	/** Erases an attachment and shows the ticket as it now stands. */
	async function deleteAttachment(attachment: SupportAttachment): Promise<void> {
		/** The response is the ticket with the removal recorded in its thread. */
		try {
			ticket = await deleteDeskAttachment(attachment.attachmentUUID);
			toastSuccess(t2('{{name}} was deleted.', { name: attachment.filename }));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete the attachment'));
		}
	}

	/** Takes someone off the ticket. */
	async function removePerson(person: SupportPerson): Promise<void> {
		changingPeople = true;
		/** The response is the ticket with the removal recorded in its thread. */
		try {
			ticket = await removeDeskParticipant(params.ticketKey as string, person.userUUID);
			toastSuccess(t2('{{name}} was removed from the ticket.', { name: person.name }));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to remove the person'));
		} finally {
			changingPeople = false;
		}
	}

	/** Reads a select and saves the field it controls. */
	function fieldChange(build: (_value: string) => SupportTicketPatch): (_event: Event) => void {
		return (event: Event) => patch(build((event.currentTarget as HTMLSelectElement).value));
	}

	/** The signed-in agent's name, for `{{AGENT_NAME}}`. */
	let agentName = $derived(`${$tokenData?.firstName ?? ''} ${$tokenData?.lastName ?? ''}`.trim());

	/** Whether the ticket is assigned to the signed-in agent. */
	let isMine = $derived(!!ticket?.assignee && ticket.assignee.userUUID === $tokenData?.userUUID);

	/** Status items — an agent may set anything except back to `new`. */
	let statusItems = $derived(
		SUPPORT_STATUSES.map((status) => ({
			value: status,
			name: $t(supportStatusLabel(status)),
			disabled: status === 'new'
		}))
	);

	/** Priority items. */
	let priorityItems = $derived(
		SUPPORT_PRIORITIES.map((priority) => ({ value: priority, name: $t(supportPriorityLabel(priority)) }))
	);

	/** Category items, with none first. */
	let categoryItems = $derived([
		{ value: '', name: $t('No category') },
		...categories.map((category) => ({ value: category.categoryUUID, name: category.name }))
	]);

	/** Assignee items, with nobody first. */
	let assigneeItems = $derived([
		{ value: '', name: $t('Unassigned') },
		...agents.map((agent) => ({ value: agent.userUUID, name: agent.name }))
	]);

	/**
	 * The key of the ticket last loaded. A plain variable, not state: the effect
	 * below compares against it, and reading state it also writes is the shape
	 * that loops.
	 */
	let loadedKey: string | undefined | null = null;

	/*
	 * (Re)loads whenever the route names a different ticket. Following a link to a
	 * linked ticket reuses this component with new params, so loading once on mount
	 * would leave the old ticket on screen under the new URL. `load` runs untracked,
	 * so nothing it reads becomes a reason to run again.
	 */
	$effect(() => {
		const key = params.ticketKey;
		/* Same ticket as on screen — nothing to do. */
		if (key === loadedKey) return;
		loadedKey = key;
		untrack(() => load());
	});
</script>

{#if loading}
	<EmptyState variant="loading" />
{:else if error || !ticket}
	<PageHeader
		title={$t('Ticket')}
		breadcrumbs={[{ label: $t('Support Desk'), href: '/Support/Desk' }, { label: params.ticketKey ?? '' }]}
	/>
	<EmptyState variant="error" description={error}>
		<RoutedButton size="sm" href="/Support/Desk">{$t('Back to the desk')}</RoutedButton>
	</EmptyState>
{:else}
	<PageHeader
		title={ticket.subject}
		breadcrumbs={[{ label: $t('Support Desk'), href: '/Support/Desk' }, { label: ticket.ticketKey }]}
	>
		{#snippet descriptionSnippet()}
			<span class="flex flex-wrap items-center gap-2 text-sm font-normal">
				<span class="font-mono">{ticket!.ticketKey}</span>
				<SupportStatusBadge status={ticket!.status} />
				<SupportPriorityBadge priority={ticket!.priority} />
				<span>· {ticket!.requester.name} · {$t('Opened')} <RelativeTime date={ticket!.created} /></span>
			</span>
		{/snippet}
	</PageHeader>

	<div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
		<div class="min-w-0 space-y-6">
			<SupportMessageThread messages={ticket.messages} audience="agent" />
			<SupportComposer
				mode="agent"
				disabled={!ticket.permissions.canReply}
				disabledReason={ticket.permissions.replyBlockedReason
					? $t(supportReasonText(ticket.permissions.replyBlockedReason))
					: ''}
				{sending}
				{uploadProgress}
				{replyTemplates}
				templateContext={{
					requesterName: ticket.requester.name,
					ticketKey: ticket.ticketKey,
					productName: ticket.application.name,
					agentName
				}}
				onsend={send}
			/>
		</div>

		<aside class="space-y-4">
			<AbstractCard class="max-w-none gap-3">
				<div class="flex items-center justify-between">
					<p class="card-label">{$t('Ticket')}</p>
					{#if saving}<Spinner size="4" />{/if}
				</div>
				<div>
					<Label for="desk-status" class="mb-1 text-xs">{$t('Status')}</Label>
					<Select
						id="desk-status"
						size="sm"
						placeholder=""
						items={statusItems}
						value={ticket.status}
						disabled={saving}
						onchange={fieldChange((value) => ({ status: value as SupportTicketStatus }))}
					/>
				</div>
				<div>
					<Label for="desk-priority" class="mb-1 text-xs">{$t('Priority')}</Label>
					<Select
						id="desk-priority"
						size="sm"
						placeholder=""
						items={priorityItems}
						value={ticket.priority}
						disabled={saving}
						onchange={fieldChange((value) => ({ priority: value as SupportTicketPriority }))}
					/>
				</div>
				<div>
					<Label for="desk-category" class="mb-1 text-xs">{$t('Category')}</Label>
					<Select
						id="desk-category"
						size="sm"
						placeholder=""
						items={categoryItems}
						value={ticket.category?.categoryUUID ?? ''}
						disabled={saving}
						onchange={fieldChange((value) => ({ categoryUUID: value || null }))}
					/>
				</div>
				<div>
					<div class="mb-1 flex items-center justify-between">
						<Label for="desk-assignee" class="text-xs">{$t('Assignee')}</Label>
						{#if !isMine && $tokenData?.userUUID}
							<button
								type="button"
								class="text-primary-600 dark:text-primary-400 text-xs font-medium hover:underline"
								disabled={saving}
								onclick={() => patch({ assigneeUUID: $tokenData!.userUUID })}
							>
								{$t('Assign to me')}
							</button>
						{/if}
					</div>
					<Select
						id="desk-assignee"
						size="sm"
						placeholder=""
						items={assigneeItems}
						value={ticket.assignee?.userUUID ?? ''}
						disabled={saving}
						onchange={fieldChange((value) => ({ assigneeUUID: value || null }))}
					/>
				</div>
			</AbstractCard>

			<AbstractCard class="max-w-none gap-2">
				<p class="card-label">{$t('Service level')}</p>
				<SupportSLATimer
					label={$t('First response')}
					dueAt={ticket.firstResponseDueAt}
					isBreached={ticket.isFirstResponseBreached}
					completedAt={ticket.firstResponseAt}
				/>
				<SupportSLATimer
					label={$t('Resolution')}
					dueAt={ticket.resolutionDueAt}
					isBreached={ticket.isResolutionBreached}
					isPaused={ticket.isSLAPaused}
					completedAt={ticket.resolvedAt}
				/>
			</AbstractCard>

			<AbstractCard class="max-w-none gap-3 text-sm">
				<p class="card-label">{$t('Requester')}</p>
				<div>
					{#if ticket.requester.id !== null}
						<RoutedLink to="/Users/{ticket.requester.id}">
							<span class="font-medium">{ticket.requester.name}</span>
						</RoutedLink>
					{:else}
						<span class="font-medium">{ticket.requester.name}</span>
					{/if}
					{#if ticket.requester.email}
						<p class="text-default break-all">{ticket.requester.email}</p>
					{/if}
				</div>
				<dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
					<dt class="text-default">{$t('Product')}</dt>
					<dd class="flex items-center gap-1.5">
						<LicenseAppIcon
							iconImageName={ticket.application.iconImageName}
							icon={ticket.application.icon}
							name={ticket.application.name}
							class="h-4 w-4"
						/>{ticket.application.name}
					</dd>
					<dt class="text-default">{$t('License')}</dt>
					<dd class="font-mono text-xs break-all">{ticket.licenseUUID ?? '—'}</dd>
					<dt class="text-default">{$t('Version')}</dt>
					<dd>{ticket.productVersion || '—'}</dd>
					<dt class="text-default">{$t('System')}</dt>
					<dd>{ticket.operatingSystem || '—'}</dd>
					{#if ticket.lastCustomerActivityAt}
						<dt class="text-default">{$t('Last heard')}</dt>
						<dd><RelativeTime date={ticket.lastCustomerActivityAt} /></dd>
					{/if}
				</dl>
				<SupportTicketLinks
					followUpOf={ticket.followUpOf}
					followUps={ticket.followUps}
					basePath="/Support/Desk/Tickets"
					audience="agent"
				/>
			</AbstractCard>

			<SupportAttachmentsPanel messages={ticket.messages} audience="agent" ondelete={deleteAttachment} />

			<SupportPeopleCard
				requester={ticket.requester}
				participants={ticket.participants}
				canManage={ticket.permissions.canManageParticipants}
				busy={changingPeople}
				onadd={addPerson}
				onremove={removePerson}
			/>

			{#if ticket.satisfactionRating !== null}
				<AbstractCard class="max-w-none gap-2">
					<p class="card-label">{$t('Satisfaction')}</p>
					<SupportRating rating={ticket.satisfactionRating} comment={ticket.satisfactionComment} />
				</AbstractCard>
			{/if}

			<Button size="sm" color="alternative" class="w-full" onclick={refresh}>
				<i class="fa-solid fa-rotate me-2"></i>{$t('Refresh')}
			</Button>
		</aside>
	</div>
{/if}
